import Repository from './repository';
import { useAuthStore } from '@/stores/auth';
import { pinia } from '@/stores/pinia';
import { ModuleRole } from '@/common/rbac';

const resource = '/utility';

// GET /constants/ is guarded with platform:user on the backend, but the generic
// lookup data it returns (countries, titles, genders, nationalities, entity
// types) is needed by Case / AML / Forms / POA pages that are gated at their own
// module-viewer level. A module-only user (aml:viewer, case:operator,
// forms:viewer, poa:admin, ...) passes those route guards, mounts the view and
// then 403s on this call, breaking the dropdowns and raising an error toast.
// Mirror the backend guard here: without platform:user we resolve to the same
// envelope shape with empty collections, so the pages stay usable and the
// affected dropdowns are simply empty. Keys mirror the backend payload.
const emptyConstants = () => ({
	data: {
		data: {
			regions: [],
			countries: [],
			nationalities: [],
			titles: [],
			genders: [],
			entityTypes: [],
			entityStatuses: [],
		},
	},
});

export default {
	async moduleroles() {
		const resp = await Repository.getWithAuth(`${resource}/moduleroles`);
		// Unwrap the standard { success, data, errors } envelope so callers
		// receive the payload ({ moduleRoles }) directly, matching the other
		// utility methods (e.g. nationalities/countrycodes) and every consumer
		// that reads body.moduleRoles.
		return resp.data.data;
	},
	async constants() {
		const cached = localStorage.getItem('cached-constants');
		if (cached === null) {
			if (!useAuthStore(pinia).can(ModuleRole.PlatformUser)) {
				// Not cached and not permitted: skip the request instead of 403ing.
				// Deliberately not cached, so the real payload is fetched as soon as
				// a permitted user uses this browser.
				return emptyConstants();
			}
			const response = await Repository.getWithAuth(`/constants/`);
			localStorage.setItem('cached-constants', JSON.stringify(response));
			return response;
		}
		return JSON.parse(cached);
	},
	async nationalities() {
		const response = await this.constants();
		return { data: { nationalities: response.data.data.nationalities } };
	},
	async countrycodes() {
		const response = await this.constants();
		return { data: { countryCodes: response.data.data.countries } };
	},
	async titles() {
		const response = await this.constants();
		return { data: { titles: response.data.data.titles } };
	},
	async genders() {
		const response = await this.constants();
		return { data: { genders: response.data.data.genders } };
	},
	config(key) {
		return Repository.getWithAuth(`${resource}/config/${key}`);
	},
	downloadPDF(payload, customHeader) {
		return Repository.postWithAuth(`/pdf`, payload, customHeader);
	},
};
